'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Plug, ScrollText, Brain, Bot, Server, ChevronLeft, ChevronRight, BarChart2, Sun, Moon, Bell, BellOff, Users, AlertTriangle, Shield, Settings, BellRing, User, Activity, Wifi, LogOut, Sliders, FlaskConical, KeyRound, ShieldCheck } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import { useAlertNotifications } from '@/context/AlertNotificationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { hasPermission, ROUTE_PERMISSIONS, ROLE_LABELS, ROLE_COLORS } from '@/lib/rbac/permissions';
import type { Permission } from '@/lib/rbac/permissions';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  activeRoute?: string;
}

const navItems = [
  { key: 'nav-integrations', label: 'Integrations Hub', href: '/integrations-hub', icon: Plug, badge: null, group: 'core' },
  { key: 'nav-logs', label: 'Live Log Stream', href: '/live-log-stream', icon: ScrollText, badge: 'LIVE', group: 'core' },
  { key: 'nav-diagnosis', label: 'AI Diagnosis', href: '/ai-diagnosis-dashboard', icon: Brain, badge: null, group: 'ai' },
  { key: 'nav-resolution', label: 'Agentic Resolution', href: '/agentic-resolution', icon: Bot, badge: '3', group: 'ai' },
  { key: 'nav-llm', label: 'LLM Control Panel', href: '/llm-control-panel', icon: Sliders, badge: null, group: 'ai' },
  { key: 'nav-mlops', label: 'MLOps Dashboard', href: '/mlops-dashboard', icon: FlaskConical, badge: null, group: 'ai' },
  { key: 'nav-servers', label: 'Server Management', href: '/server-management', icon: Server, badge: null, group: 'infra' },
  { key: 'nav-incidents', label: 'Incident History', href: '/incident-history', icon: AlertTriangle, badge: null, group: 'ops' },
  { key: 'nav-audit', label: 'Audit Trail', href: '/audit-trail', icon: Shield, badge: null, group: 'ops' },
  { key: 'nav-analytics', label: 'Analytics', href: '/analytics', icon: BarChart2, badge: null, group: 'insights' },
  { key: 'nav-api-usage', label: 'API Usage', href: '/api-usage-dashboard', icon: Activity, badge: null, group: 'insights' },
  { key: 'nav-integration-health', label: 'Integration Health', href: '/integration-health', icon: Wifi, badge: null, group: 'insights' },
  { key: 'nav-api-keys', label: 'API Keys', href: '/api-keys', icon: KeyRound, badge: null, group: 'settings' },
  { key: 'nav-secrets-vault', label: 'Secrets Vault', href: '/secrets-vault', icon: ShieldCheck, badge: null, group: 'settings' },
  { key: 'nav-team', label: 'Team Members', href: '/team-members', icon: Users, badge: null, group: 'settings' },
  { key: 'nav-notifications', label: 'Notifications', href: '/notification-preferences', icon: BellRing, badge: null, group: 'settings' },
  { key: 'nav-profile', label: 'User Profile', href: '/user-profile', icon: User, badge: null, group: 'settings' },
  { key: 'nav-settings', label: 'Settings', href: '/settings', icon: Settings, badge: null, group: 'settings' },
];

const groups = [
  { key: 'group-core', id: 'core', label: 'Platform' },
  { key: 'group-ai', id: 'ai', label: 'AI Agents' },
  { key: 'group-infra', id: 'infra', label: 'Infrastructure' },
  { key: 'group-ops', id: 'ops', label: 'Operations' },
  { key: 'group-insights', id: 'insights', label: 'Insights' },
  { key: 'group-settings', id: 'settings', label: 'Settings' },
];

export default function Sidebar({ collapsed, onToggle, activeRoute }: SidebarProps) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const { theme, toggleTheme, isDark } = useTheme();
  const { startSimulation, stopSimulation } = useAlertNotifications();
  const [alertsActive, setAlertsActive] = useState(false);
  const { signOut, role, user } = useAuth();
  const router = useRouter();

  const handleLogout = async () => {
    await signOut();
    router.replace('/sign-up-login-screen');
  };

  const handleAlertToggle = () => {
    if (alertsActive) {
      stopSimulation();
      setAlertsActive(false);
    } else {
      startSimulation();
      setAlertsActive(true);
    }
  };

  /** Zero Trust: only show nav items the current role has permission to access */
  const isNavItemVisible = (href: string): boolean => {
    const requiredPermission = ROUTE_PERMISSIONS[href] as Permission | undefined;
    if (!requiredPermission) return true; // no restriction = visible
    return hasPermission(role, requiredPermission);
  };

  // Derive user display info
  const displayName = user?.metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  const roleLabel = ROLE_LABELS[role] || role;
  const roleColor = ROLE_COLORS[role] || '#64748b';

  return (
    <aside
      style={{
        width: collapsed ? '60px' : '232px',
        transition: 'width 280ms cubic-bezier(0.4, 0, 0.2, 1)',
        backgroundColor: 'var(--sidebar-bg)',
        borderRight: '1px solid var(--sidebar-border)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        overflow: 'hidden',
        position: 'relative',
        zIndex: 50,
        height: '100%',
      }}
    >
      {/* Logo */}
      <div
        style={{
          padding: collapsed ? '0 14px' : '0 20px',
          borderBottom: '1px solid var(--sidebar-border)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          height: '60px',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <AppLogo size={28} />
        {!collapsed && (
          <div style={{ overflow: 'hidden' }}>
            <span
              style={{
                fontSize: '15px',
                fontWeight: 700,
                color: 'var(--text-primary)',
                letterSpacing: '-0.01em',
                whiteSpace: 'nowrap',
                fontFamily: 'DM Sans, sans-serif',
              }}
            >
              Nexus
            </span>
            <div
              style={{
                fontSize: '10px',
                color: 'var(--text-muted)',
                marginTop: '1px',
                whiteSpace: 'nowrap',
                fontWeight: 500,
              }}
            >
              DevOps Platform
            </div>
          </div>
        )}
      </div>

      {/* System Status */}
      {!collapsed && (
        <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--sidebar-border)' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: 'rgba(34, 197, 94, 0.06)',
              border: '1px solid rgba(34, 197, 94, 0.15)',
              borderRadius: '7px',
              padding: '7px 10px',
            }}
          >
            <div
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: 'var(--accent-green)',
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: '11px', color: 'var(--accent-green)', fontWeight: 600 }}>
              All systems operational
            </span>
          </div>
        </div>
      )}

      {/* Nav — filtered by RBAC */}
      <nav style={{ flex: 1, padding: '8px 8px', overflowY: 'auto', overflowX: 'hidden' }}>
        {groups.map((group) => {
          const groupItems = navItems.filter(
            (item) => item.group === group.id && isNavItemVisible(item.href)
          );
          if (groupItems.length === 0) return null;
          return (
            <div key={group.key} style={{ marginBottom: '4px' }}>
              {!collapsed && (
                <div
                  style={{
                    fontSize: '10px',
                    fontWeight: 600,
                    color: 'var(--text-dim)',
                    letterSpacing: '0.08em',
                    padding: '10px 8px 4px',
                    textTransform: 'uppercase',
                  }}
                >
                  {group.label}
                </div>
              )}
              {collapsed && <div style={{ height: '6px' }} />}
              {groupItems.map((item) => {
                const IconComp = item.icon;
                const isActive = activeRoute === item.href;
                const isHovered = hoveredItem === item.key;

                return (
                  <div key={item.key} style={{ position: 'relative' }}>
                    <Link
                      href={item.href}
                      onMouseEnter={() => setHoveredItem(item.key)}
                      onMouseLeave={() => setHoveredItem(null)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '9px',
                        padding: collapsed ? '9px 0' : '8px 10px',
                        borderRadius: '7px',
                        marginBottom: '1px',
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        backgroundColor: isActive
                          ? 'var(--surface-active)'
                          : isHovered
                          ? 'var(--surface-hover)'
                          : 'transparent',
                        border: isActive
                          ? '1px solid rgba(79, 126, 248, 0.2)'
                          : '1px solid transparent',
                        transition: 'all 150ms ease',
                        textDecoration: 'none',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <IconComp
                        size={15}
                        style={{
                          color: isActive ? 'var(--accent-blue)' : 'var(--text-muted)',
                          flexShrink: 0,
                          transition: 'color 150ms',
                        }}
                      />
                      {!collapsed && (
                        <>
                          <span
                            style={{
                              fontSize: '13px',
                              fontWeight: isActive ? 600 : 400,
                              color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
                              flex: 1,
                            }}
                          >
                            {item.label}
                          </span>
                          {item.badge && (
                            <span
                              style={{
                                fontSize: '10px',
                                fontWeight: 600,
                                padding: '1px 6px',
                                borderRadius: '20px',
                                backgroundColor:
                                  item.badge === 'LIVE' ? 'rgba(34, 197, 94, 0.12)' : 'rgba(244, 63, 94, 0.12)',
                                color: item.badge === 'LIVE' ? 'var(--accent-green)' : 'var(--accent-red)',
                                border: `1px solid ${item.badge === 'LIVE' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(244, 63, 94, 0.2)'}`,
                              }}
                            >
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                    </Link>

                    {/* Collapsed tooltip */}
                    {collapsed && isHovered && (
                      <div
                        style={{
                          position: 'absolute',
                          left: '68px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          backgroundColor: 'var(--bg-elevated)',
                          border: '1px solid var(--border-bright)',
                          borderRadius: '7px',
                          padding: '6px 10px',
                          whiteSpace: 'nowrap',
                          zIndex: 100,
                          pointerEvents: 'none',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                        }}
                      >
                        <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: 500 }}>{item.label}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Bottom section */}
      <div style={{ borderTop: '1px solid var(--sidebar-border)', padding: '8px' }}>
        {/* User info with real role */}
        {!collapsed && (
          <div
            style={{
              padding: '8px 10px',
              marginBottom: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              borderRadius: '7px',
              backgroundColor: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                width: '30px',
                height: '30px',
                borderRadius: '8px',
                backgroundColor: `${roleColor}20`,
                border: `1px solid ${roleColor}30`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <span style={{ fontSize: '11px', color: roleColor, fontWeight: 700 }}>{initials}</span>
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {displayName}
              </div>
              <div style={{ fontSize: '10px', color: roleColor, whiteSpace: 'nowrap', fontWeight: 600 }}>
                {roleLabel}
              </div>
            </div>
          </div>
        )}

        {/* Controls row */}
        <div
          style={{
            display: 'flex',
            gap: '4px',
            padding: '4px 0',
            marginBottom: '4px',
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
        >
          <button
            onClick={toggleTheme}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: collapsed ? 0 : '6px',
              padding: collapsed ? '8px' : '7px 10px',
              borderRadius: '7px',
              backgroundColor: 'transparent',
              border: '1px solid var(--border)',
              cursor: 'pointer',
              color: 'var(--text-muted)',
              transition: 'all 150ms',
              flex: collapsed ? 'none' : 1,
              fontSize: '12px',
              fontWeight: 500,
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--surface-hover)';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border-bright)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)';
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)';
            }}
          >
            {isDark ? <Sun size={14} /> : <Moon size={14} />}
            {!collapsed && <span>{isDark ? 'Light' : 'Dark'}</span>}
          </button>

          <button
            onClick={handleAlertToggle}
            title={alertsActive ? 'Stop alert simulation' : 'Start alert simulation'}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '7px 8px',
              borderRadius: '7px',
              backgroundColor: alertsActive ? 'rgba(244, 63, 94, 0.08)' : 'transparent',
              border: alertsActive ? '1px solid rgba(244, 63, 94, 0.25)' : '1px solid var(--border)',
              cursor: 'pointer',
              color: alertsActive ? 'var(--accent-red)' : 'var(--text-muted)',
              transition: 'all 150ms',
              flexShrink: 0,
            }}
          >
            {alertsActive ? <BellOff size={14} /> : <Bell size={14} />}
          </button>
        </div>

        <button
          onClick={handleLogout}
          title="Logout"
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: '8px',
            padding: '8px 10px',
            borderRadius: '7px',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-muted)',
            transition: 'all 150ms',
            marginBottom: '2px',
            fontSize: '12px',
            fontWeight: 500,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(244, 63, 94, 0.06)';
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--accent-red)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)';
          }}
        >
          <LogOut size={14} />
          {!collapsed && <span>Sign out</span>}
        </button>

        <button
          onClick={onToggle}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: '8px',
            padding: '8px 10px',
            borderRadius: '7px',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-muted)',
            transition: 'all 150ms',
            fontSize: '12px',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--surface-hover)';
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-primary)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)';
          }}
        >
          {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </aside>
  );
}